import * as React from "react";

import type { QueryTarget } from "@/shared/domain/query";
import type { ViewRecord, ViewVisibility } from "@/shared/domain/views";
import { useCreateView, useUpdateView } from "@/shared/domain/views/hooks";

import type { ResourceView } from "../useResourceView";
import { viewStateToDefinition } from "../viewSerialization";

import { isViewDirty } from "./isViewDirty";
import type { LoadedView } from "./useLoadedView";

export interface SaveView {
  /**
   * Save the current state as a brand-new View.
   *
   * Marks it loaded on success — the caller does not, and cannot, do this
   * itself. See {@link UseSaveViewOptions.session}.
   */
  createView: (args: {
    name: string;
    visibility: ViewVisibility;
  }) => Promise<ViewRecord>;
  /** Overwrite the loaded View's definition with the current state, and keep it loaded. */
  updateLoadedView: () => Promise<ViewRecord>;
  /** True while either save is in flight. */
  isSaving: boolean;
  /**
   * True when a View is loaded and live state no longer matches it. `false`
   * with nothing loaded — ad-hoc filters are not unsaved changes.
   */
  isDirty: boolean;
}

/** The part of `useLoadedView` a save needs: the bracket, and what is loaded. */
export type SaveSession = Pick<
  LoadedView,
  "beginSave" | "completeSave" | "loadedView"
>;

export interface UseSaveViewOptions<TData> {
  view: ResourceView<TData>;
  target: QueryTarget;
  /**
   * The loaded-View session this save belongs to.
   *
   * Passed in whole rather than as an `onSaved` callback, because the marking
   * is not the caller's decision to make. A save is asynchronous, and whether
   * its result should still be marked loaded depends on what happened while it
   * was out — which only `useLoadedView` knows. Handing that back to a
   * component invited it to be got wrong, and it was: an org switch mid-save
   * used to restore the old org's View.
   *
   * Kept as a dependency rather than moving the mutations into `useLoadedView`
   * so that `useCreateView`/`useUpdateView` stay behind the `release-views`
   * gate. Every list mounts `useLoadedView`; only the gated controls mount this.
   */
  session: SaveSession;
}

/**
 * Capture the resource view's current state as a View.
 *
 * State is assembled here rather than passed in: it spans two owners (filters,
 * and the table's sort/paging/columns), and a caller doing it by hand can
 * forget one. `isDirty` is derived — see `isViewDirty`.
 */
export function useSaveView<TData>({
  view,
  target,
  session,
}: UseSaveViewOptions<TData>): SaveView {
  const { beginSave, completeSave, loadedView } = session;
  const create = useCreateView();
  const update = useUpdateView();

  const { filters, table } = view;
  const { sort, pageSize, visibleColumnIds } = table;

  const currentState = React.useMemo(
    () => ({
      filters,
      sort,
      pageSize,
      visibleColumns: [...visibleColumnIds],
    }),
    [filters, sort, pageSize, visibleColumnIds],
  );

  const createAsync = create.mutateAsync;
  const updateAsync = update.mutateAsync;

  // Both saves follow one shape: open the bracket *before* the request, so the
  // token describes the world the save was built in, then let the session
  // decide whether that world still holds. Neither the token nor the decision
  // is exposed — a save that marked its result loaded only sometimes, depending
  // on a caller remembering two calls in the right order, is the bug this
  // replaces.
  const createView = React.useCallback(
    async ({
      name,
      visibility,
    }: {
      name: string;
      visibility: ViewVisibility;
    }) => {
      const token = beginSave();
      const created = await createAsync({
        name,
        target,
        visibility,
        definition: viewStateToDefinition(currentState),
      });
      completeSave(created, token);
      return created;
    },
    [beginSave, completeSave, createAsync, currentState, target],
  );

  const updateLoadedView = React.useCallback(async () => {
    if (loadedView === null) {
      // Unreachable through the UI. Reject rather than silently create, so a
      // programmer error does not leave a stray View in a customer's org.
      throw new Error("No View is loaded to save to");
    }
    const token = beginSave();
    const updated = await updateAsync({
      viewId: loadedView.view_id,
      updates: { definition: viewStateToDefinition(currentState) },
    });
    // Hand the saved record back through the session, or `isDirty` keeps
    // comparing against the pre-save definition and the control stays on screen
    // after a success.
    completeSave(updated, token);
    return updated;
  }, [beginSave, completeSave, currentState, loadedView, updateAsync]);

  const isDirty =
    loadedView !== null && isViewDirty(loadedView.definition, currentState);

  return {
    createView,
    updateLoadedView,
    isSaving: create.isPending || update.isPending,
    isDirty,
  };
}
